import React, { useState, useEffect, useCallback, useRef } from 'react';
import { visitorLogAPI, feedbackAPI } from '../api';
import { useAuth } from '../AuthContext';
import { FaUsers, FaStar, FaChartLine, FaQrcode, FaDownload, FaComments, FaUserTie, FaExclamationTriangle, FaSearch, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ITEMS_PER_PAGE = 20;

const ManagerDashboard = () => {
  const { home } = useAuth();
  const [visitorStats, setVisitorStats] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [staffFeedbackStats, setStaffFeedbackStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [staffFeedbacks, setStaffFeedbacks] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedStaffFeedback, setSelectedStaffFeedback] = useState(null);

  // Resolve states
  const [resolvingLog, setResolvingLog] = useState(null);
  const [resolveLogData, setResolveLogData] = useState({ status: '', addressFeedback: '' });
  const [resolvingFeedback, setResolvingFeedback] = useState(null);
  const [resolveFeedbackData, setResolveFeedbackData] = useState({ status: '', adminNotes: '' });
  const [resolvingStaffFeedback, setResolvingStaffFeedback] = useState(null);
  const [resolveStaffData, setResolveStaffData] = useState({ status: '', adminNotes: '' });

  // Filter states
  const [staffFilter, setStaffFilter] = useState({ search: '', type: '', severity: '', status: '', department: '' });
  const [visitorFilter, setVisitorFilter] = useState({ search: '', resolved: '', rating: '' });
  const [feedbackFilter, setFeedbackFilter] = useState({ search: '', type: '', serviceArea: '' });

  // Pagination states
  const [staffVisible, setStaffVisible] = useState(ITEMS_PER_PAGE);
  const [visitorVisible, setVisitorVisible] = useState(ITEMS_PER_PAGE);
  const [feedbackVisible, setFeedbackVisible] = useState(ITEMS_PER_PAGE);

  const staffScrollRef = useRef(null);
  const visitorScrollRef = useRef(null);
  const feedbackScrollRef = useRef(null);

  // Scroll handler for infinite scroll
  const handleScroll = useCallback((ref, visibleCount, setVisible, totalCount) => {
    if (!ref.current) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    if (scrollTop + clientHeight >= scrollHeight - 50 && visibleCount < totalCount) {
      setVisible(prev => Math.min(prev + ITEMS_PER_PAGE, totalCount));
    }
  }, []);

  useEffect(() => {
    const staffEl = staffScrollRef.current;
    const visitorEl = visitorScrollRef.current;
    const feedbackEl = feedbackScrollRef.current;

    const onStaffScroll = () => handleScroll(staffScrollRef, staffVisible, setStaffVisible, filteredStaffFeedbacks.length);
    const onVisitorScroll = () => handleScroll(visitorScrollRef, visitorVisible, setVisitorVisible, filteredLogs.length);
    const onFeedbackScroll = () => handleScroll(feedbackScrollRef, feedbackVisible, setFeedbackVisible, filteredFeedbacks.length);

    staffEl?.addEventListener('scroll', onStaffScroll);
    visitorEl?.addEventListener('scroll', onVisitorScroll);
    feedbackEl?.addEventListener('scroll', onFeedbackScroll);

    return () => {
      staffEl?.removeEventListener('scroll', onStaffScroll);
      visitorEl?.removeEventListener('scroll', onVisitorScroll);
      feedbackEl?.removeEventListener('scroll', onFeedbackScroll);
    };
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [vStatsRes, fStatsRes, logsRes, feedbacksRes, staffFeedbacksRes, staffStatsRes] = await Promise.all([
        visitorLogAPI.getStats(),
        feedbackAPI.getStats(),
        visitorLogAPI.getLogs(),
        feedbackAPI.getFeedbacks(),
        axios.get(`${API_URL}/staff-feedback`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/staff-feedback/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVisitorStats(vStatsRes.data);
      setFeedbackStats(fStatsRes.data);
      setLogs(logsRes.data);
      setFeedbacks(feedbacksRes.data);
      setStaffFeedbacks(staffFeedbacksRes.data);
      setStaffFeedbackStats(staffStatsRes.data);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    }
  };

  const handleResolveLog = async () => {
    try {
      await visitorLogAPI.resolveLog(resolvingLog._id, resolveLogData);
      toast.success('Visitor log updated successfully!');
      setResolvingLog(null);
      setResolveLogData({ status: '', addressFeedback: '' });
      setSelectedLog(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update visitor log');
    }
  };

  const handleResolveFeedback = async () => {
    try {
      await feedbackAPI.updateFeedback(resolvingFeedback._id, resolveFeedbackData);
      toast.success('Resident feedback updated successfully!');
      setResolvingFeedback(null);
      setResolveFeedbackData({ status: '', adminNotes: '' });
      setSelectedFeedback(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update feedback');
    }
  };

  const handleResolveStaffFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/staff-feedback/${resolvingStaffFeedback._id}`, resolveStaffData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff feedback updated successfully!');
      setResolvingStaffFeedback(null);
      setResolveStaffData({ status: '', adminNotes: '' });
      setSelectedStaffFeedback(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update staff feedback');
    }
  };

  // Sort latest first + filter logic
  const sortByDateDesc = (a, b, dateField = 'submittedAt') => new Date(b[dateField]) - new Date(a[dateField]);

  const filteredStaffFeedbacks = staffFeedbacks
    .slice()
    .sort((a, b) => sortByDateDesc(a, b))
    .filter(f => {
      const search = staffFilter.search.toLowerCase();
      if (search && !f.fullName?.toLowerCase().includes(search) && !f.department?.toLowerCase().includes(search) && !f.feedbackDetails?.toLowerCase().includes(search)) return false;
      if (staffFilter.type && f.feedbackType !== staffFilter.type) return false;
      if (staffFilter.severity && f.severityLevel !== staffFilter.severity) return false;
      if (staffFilter.status && f.status !== staffFilter.status) return false;
      if (staffFilter.department && f.department !== staffFilter.department) return false;
      return true;
    });

  const filteredLogs = logs
    .slice()
    .sort((a, b) => sortByDateDesc(a, b, 'visitDate'))
    .filter(l => {
      const search = visitorFilter.search.toLowerCase();
      if (search && !l.fullName?.toLowerCase().includes(search) && !l.residentName?.toLowerCase().includes(search) && !l.phoneNumber?.includes(search)) return false;
      if (visitorFilter.resolved && l.purposeResolved !== visitorFilter.resolved) return false;
      if (visitorFilter.rating && l.experienceRating !== Number(visitorFilter.rating)) return false;
      return true;
    });

  const filteredFeedbacks = feedbacks
    .slice()
    .sort((a, b) => sortByDateDesc(a, b))
    .filter(f => {
      const search = feedbackFilter.search.toLowerCase();
      if (search && !f.residentFullName?.toLowerCase().includes(search) && !f.feedbackDetails?.toLowerCase().includes(search) && !f.serviceArea?.toLowerCase().includes(search)) return false;
      if (feedbackFilter.type && f.feedbackType !== feedbackFilter.type) return false;
      if (feedbackFilter.serviceArea && f.serviceArea !== feedbackFilter.serviceArea) return false;
      return true;
    });

  // Unique values for filter dropdowns
  const uniqueDepartments = [...new Set(staffFeedbacks.map(f => f.department).filter(Boolean))];
  const uniqueServiceAreas = [...new Set(feedbacks.map(f => f.serviceArea).filter(Boolean))];

  // Reset pagination when filters change
  useEffect(() => { setStaffVisible(ITEMS_PER_PAGE); }, [staffFilter]);
  useEffect(() => { setVisitorVisible(ITEMS_PER_PAGE); }, [visitorFilter]);
  useEffect(() => { setFeedbackVisible(ITEMS_PER_PAGE); }, [feedbackFilter]);

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = home.qrCode;
    link.download = `${home.title.replace(/\s+/g, '-')}-QR-Code.png`;
    link.click();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Low': return '#10b981';
      case 'Medium': return '#f59e0b';
      case 'High': return '#ef4444';
      case 'Critical': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444'];

  const purposeData = visitorStats?.purposeResolved
    ? [
        { name: 'Yes', value: visitorStats.purposeResolved.yes },
        { name: 'Partially', value: visitorStats.purposeResolved.partially },
        { name: 'No', value: visitorStats.purposeResolved.no },
      ]
    : [];

  return (
    <>
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaChartLine /> {home?.title} Dashboard</h2>
            <button onClick={() => setShowQR(true)} className="btn btn-primary">
              <FaQrcode /> View QR Code
            </button>
          </div>
          <div className="card-body">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><FaUsers /></div>
                <div className="stat-content">
                  <div className="stat-label">Visitor Logs</div>
                  <div className="stat-value">{visitorStats?.totalLogs || 0}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaStar /></div>
                <div className="stat-content">
                  <div className="stat-label">Avg Rating</div>
                  <div className="stat-value">{visitorStats?.averageRating || 0}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaComments /></div>
                <div className="stat-content">
                  <div className="stat-label">Resident Feedbacks</div>
                  <div className="stat-value">{feedbackStats?.totalFeedbacks || 0}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><FaUserTie /></div>
                <div className="stat-content">
                  <div className="stat-label">Staff Feedbacks</div>
                  <div className="stat-value">{staffFeedbackStats?.totalFeedbacks || 0}</div>
                </div>
              </div>
              <div className="stat-card" style={{ background: staffFeedbackStats?.escalatedCount > 0 ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #1A2D8B 0%, #152469 100%)' }}>
                <div className="stat-icon"><FaExclamationTriangle /></div>
                <div className="stat-content">
                  <div className="stat-label">Escalated Issues</div>
                  <div className="stat-value">{staffFeedbackStats?.escalatedCount || 0}</div>
                </div>
              </div>
            </div>

            {visitorStats && visitorStats.totalLogs > 0 && purposeData.some(d => d.value > 0) && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Purpose Resolved</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={purposeData} cx="50%" cy="50%" labelLine={false} label={(entry) => entry.name} outerRadius={80} dataKey="value">
                      {purposeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Staff Feedback Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaUserTie /> Staff Feedback</h2>
            <span className="badge badge-primary">{filteredStaffFeedbacks.length} of {staffFeedbacks.length}</span>
          </div>
          <div className="card-body">
            {staffFeedbacks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaUserTie /></div>
                <h3 className="empty-title">No Staff Feedback Yet</h3>
                <p className="empty-text">Staff feedback will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search name, department..." className="filter-input" value={staffFilter.search} onChange={e => setStaffFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={staffFilter.type} onChange={e => setStaffFilter(f => ({ ...f, type: e.target.value }))}>
                    <option value="">All Types</option>
                    <option value="Suggestion">Suggestion</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Appreciation">Appreciation</option>
                    <option value="Safety Concern">Safety Concern</option>
                    <option value="Other">Other</option>
                  </select>
                  <select className="filter-select" value={staffFilter.severity} onChange={e => setStaffFilter(f => ({ ...f, severity: e.target.value }))}>
                    <option value="">All Severity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                  <select className="filter-select" value={staffFilter.status} onChange={e => setStaffFilter(f => ({ ...f, status: e.target.value }))}>
                    <option value="">All Status</option>
                    <option value="New">New</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Escalated">Escalated</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                  {uniqueDepartments.length > 0 && (
                    <select className="filter-select" value={staffFilter.department} onChange={e => setStaffFilter(f => ({ ...f, department: e.target.value }))}>
                      <option value="">All Departments</option>
                      {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  )}
                </div>
                <div className="table-scroll-container" ref={staffScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Staff Name</th>
                        <th>Department</th>
                        <th>Type</th>
                        <th>Severity</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaffFeedbacks.slice(0, staffVisible).map((feedback) => (
                        <tr key={feedback._id} style={feedback.escalationFlag ? { background: '#fef2f2' } : {}}>
                          <td>{new Date(feedback.submittedAt).toLocaleDateString()}</td>
                          <td>
                            <strong>{feedback.fullName}</strong>
                            {feedback.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td>{feedback.department}</td>
                          <td>
                            <span className={`badge ${
                              feedback.feedbackType === 'Safety Concern' || feedback.feedbackType === 'Complaint' ? 'badge-danger' :
                              feedback.feedbackType === 'Appreciation' ? 'badge-success' :
                              'badge-primary'
                            }`}>
                              {feedback.feedbackType}
                            </span>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: getSeverityColor(feedback.severityLevel),
                                color: 'white'
                              }}
                            >
                              {feedback.severityLevel}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              feedback.status === 'New' ? 'badge-primary' :
                              feedback.status === 'Under Review' ? 'badge-warning' :
                              feedback.status === 'Escalated' ? 'badge-danger' :
                              feedback.status === 'Resolved' ? 'badge-success' :
                              'badge-secondary'
                            }`}>
                              {feedback.status}
                            </span>
                            {feedback.escalationFlag && (
                              <FaExclamationTriangle style={{ marginLeft: '0.5rem', color: '#dc2626' }} title="Escalated" />
                            )}
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedStaffFeedback(feedback)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingStaffFeedback(feedback);
                              setResolveStaffData({ status: feedback.status || 'New', adminNotes: feedback.adminNotes || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredStaffFeedbacks.length === 0 && (
                    <div className="filter-empty">No staff feedback matches your filters</div>
                  )}
                  {staffVisible < filteredStaffFeedbacks.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaUsers /> Visitor Logs</h2>
            <span className="badge badge-primary">{filteredLogs.length} of {logs.length}</span>
          </div>
          <div className="card-body">
            {logs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaUsers /></div>
                <h3 className="empty-title">No Visitor Logs Yet</h3>
                <p className="empty-text">Share your QR code to start receiving logs</p>
                <button onClick={() => setShowQR(true)} className="btn btn-primary">
                  <FaQrcode /> View QR Code
                </button>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search visitor, resident, phone..." className="filter-input" value={visitorFilter.search} onChange={e => setVisitorFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={visitorFilter.resolved} onChange={e => setVisitorFilter(f => ({ ...f, resolved: e.target.value }))}>
                    <option value="">All Resolved</option>
                    <option value="Yes">Yes</option>
                    <option value="Partially">Partially</option>
                    <option value="No">No</option>
                  </select>
                  <select className="filter-select" value={visitorFilter.rating} onChange={e => setVisitorFilter(f => ({ ...f, rating: e.target.value }))}>
                    <option value="">All Ratings</option>
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>
                </div>
                <div className="table-scroll-container" ref={visitorScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Visitor</th>
                        <th>Phone</th>
                        <th>Resident</th>
                        <th>Rating</th>
                        <th>Resolved</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.slice(0, visitorVisible).map((log) => (
                        <tr key={log._id}>
                          <td>{new Date(log.visitDate).toLocaleDateString()}</td>
                          <td>
                            <strong>{log.fullName}</strong>
                            {log.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td>{log.phoneNumber}</td>
                          <td>{log.residentName}</td>
                          <td><strong>{log.experienceRating}</strong> / 5</td>
                          <td>
                            <span className={`badge ${log.purposeResolved === 'Yes' ? 'badge-success' : log.purposeResolved === 'No' ? 'badge-danger' : 'badge-warning'}`}>
                              {log.purposeResolved}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${log.status === 'Resolved' || log.status === 'Closed' ? 'badge-success' : log.status === 'In Progress' ? 'badge-warning' : 'badge-secondary'}`}>
                              {log.status || 'Pending'}
                            </span>
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedLog(log)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingLog(log);
                              setResolveLogData({ status: log.status || 'Pending', addressFeedback: log.addressFeedback || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredLogs.length === 0 && (
                    <div className="filter-empty">No visitor logs match your filters</div>
                  )}
                  {visitorVisible < filteredLogs.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><FaStar /> Resident Feedback</h2>
            <span className="badge badge-primary">{filteredFeedbacks.length} of {feedbacks.length}</span>
          </div>
          <div className="card-body">
            {feedbacks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><FaStar /></div>
                <h3 className="empty-title">No Feedback Yet</h3>
                <p className="empty-text">Feedback will appear here once submitted</p>
              </div>
            ) : (
              <>
                <div className="filter-bar">
                  <div className="filter-input-wrapper">
                    <FaSearch className="filter-search-icon" />
                    <input type="text" placeholder="Search resident, details, area..." className="filter-input" value={feedbackFilter.search} onChange={e => setFeedbackFilter(f => ({ ...f, search: e.target.value }))} />
                  </div>
                  <select className="filter-select" value={feedbackFilter.type} onChange={e => setFeedbackFilter(f => ({ ...f, type: e.target.value }))}>
                    <option value="">All Types</option>
                    <option value="Suggestion">Suggestion</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Appreciation">Appreciation</option>
                    <option value="Other">Other</option>
                  </select>
                  {uniqueServiceAreas.length > 0 && (
                    <select className="filter-select" value={feedbackFilter.serviceArea} onChange={e => setFeedbackFilter(f => ({ ...f, serviceArea: e.target.value }))}>
                      <option value="">All Service Areas</option>
                      {uniqueServiceAreas.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  )}
                </div>
                <div className="table-scroll-container" ref={feedbackScrollRef}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Resident</th>
                        <th>Type</th>
                        <th>Rating</th>
                        <th>Service Area</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFeedbacks.slice(0, feedbackVisible).map((feedback) => (
                        <tr key={feedback._id}>
                          <td>{new Date(feedback.submittedAt).toLocaleDateString()}</td>
                          <td>
                            <strong>{feedback.residentFullName}</strong>
                            {feedback.isAnonymous && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>Anonymous</span>}
                          </td>
                          <td>
                            <span className={`badge ${feedback.feedbackType === 'Complaint' ? 'badge-danger' : feedback.feedbackType === 'Appreciation' ? 'badge-success' : 'badge-primary'}`}>
                              {feedback.feedbackType}
                            </span>
                          </td>
                          <td>{feedback.experienceRating}</td>
                          <td>{feedback.serviceArea}</td>
                          <td>
                            <span className={`badge ${feedback.status === 'Resolved' || feedback.status === 'Closed' ? 'badge-success' : feedback.status === 'In Progress' ? 'badge-warning' : feedback.status === 'Acknowledged' ? 'badge-primary' : 'badge-secondary'}`}>
                              {feedback.status || 'Pending'}
                            </span>
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setSelectedFeedback(feedback)} className="btn btn-primary btn-sm">
                              <FaEye /> View
                            </button>
                            <button onClick={() => {
                              setResolvingFeedback(feedback);
                              setResolveFeedbackData({ status: feedback.status || 'Pending', adminNotes: feedback.adminNotes || '' });
                            }} className="btn btn-success btn-sm" style={{ background: '#10b981', borderColor: '#10b981' }}>
                              Resolve
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredFeedbacks.length === 0 && (
                    <div className="filter-empty">No resident feedback matches your filters</div>
                  )}
                  {feedbackVisible < filteredFeedbacks.length && (
                    <div className="scroll-hint">Scroll down for more...</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}

      {showQR && (
        <div className="modal-overlay" onClick={() => setShowQR(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Your QR Code</h2>
            </div>
            <div className="modal-body">
              <div className="qr-display">
                <div className="qr-code">
                  <img src={home?.qrCode} alt="QR Code" />
                </div>
                <div className="qr-info">
                  <p><strong>Home:</strong> {home?.title}</p>
                  <p><strong>Home ID:</strong> {home?.homeId}</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
                    Share this QR code to collect visitor logs and feedback
                  </p>
                </div>
                <button onClick={downloadQRCode} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  <FaDownload /> Download QR Code
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowQR(false)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Feedback Detail Modal - NEW */}
      {selectedStaffFeedback && (
        <div className="modal-overlay" onClick={() => setSelectedStaffFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Staff Feedback Details</h2>
              {selectedStaffFeedback.escalationFlag && (
                <span className="badge badge-danger" style={{ marginLeft: '1rem' }}>
                  <FaExclamationTriangle /> ESCALATED
                </span>
              )}
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Staff Information */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Staff Information
                    {selectedStaffFeedback.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous Submission</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Full Name:</strong><br />
                      {selectedStaffFeedback.fullName}
                    </div>
                    <div>
                      <strong>Employee ID:</strong><br />
                      {selectedStaffFeedback.employeeId}
                    </div>
                    <div>
                      <strong>Department:</strong><br />
                      {selectedStaffFeedback.department}
                    </div>
                    {selectedStaffFeedback.role && (
                      <div>
                        <strong>Role:</strong><br />
                        {selectedStaffFeedback.role}
                      </div>
                    )}
                    <div>
                      <strong>Employment Type:</strong><br />
                      {selectedStaffFeedback.employmentType}
                    </div>
                  </div>
                </div>

                {/* Feedback Details */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Submitted:</strong><br />
                      {new Date(selectedStaffFeedback.submittedAt).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>Type:</strong><br />
                      <span className={`badge ${
                        selectedStaffFeedback.feedbackType === 'Safety Concern' || selectedStaffFeedback.feedbackType === 'Complaint' ? 'badge-danger' : 
                        selectedStaffFeedback.feedbackType === 'Appreciation' ? 'badge-success' : 
                        'badge-primary'
                      }`}>
                        {selectedStaffFeedback.feedbackType}
                      </span>
                    </div>
                    <div>
                      <strong>Severity:</strong><br />
                      <span className="badge" style={{ background: getSeverityColor(selectedStaffFeedback.severityLevel), color: 'white' }}>
                        {selectedStaffFeedback.severityLevel}
                      </span>
                    </div>
                    {selectedStaffFeedback.relatedArea && (
                      <div>
                        <strong>Related Area:</strong><br />
                        {selectedStaffFeedback.relatedArea}
                      </div>
                    )}
                    <div>
                      <strong>Status:</strong><br />
                      <span className={`badge ${
                        selectedStaffFeedback.status === 'New' ? 'badge-primary' :
                        selectedStaffFeedback.status === 'Under Review' ? 'badge-warning' :
                        selectedStaffFeedback.status === 'Escalated' ? 'badge-danger' :
                        selectedStaffFeedback.status === 'Resolved' ? 'badge-success' :
                        'badge-secondary'
                      }`}>
                        {selectedStaffFeedback.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Incident Information */}
                {(selectedStaffFeedback.incidentDate || selectedStaffFeedback.incidentTime) && (
                  <div>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Incident Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {selectedStaffFeedback.incidentDate && (
                        <div>
                          <strong>Date:</strong><br />
                          {new Date(selectedStaffFeedback.incidentDate).toLocaleDateString()}
                        </div>
                      )}
                      {selectedStaffFeedback.incidentTime && (
                        <div>
                          <strong>Time:</strong><br />
                          {selectedStaffFeedback.incidentTime}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed Feedback */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Detailed Description</h3>
                  <div style={{ padding: '1.5rem', background: 'var(--gray-50)', borderRadius: '0.5rem', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                    {selectedStaffFeedback.feedbackDetails}
                  </div>
                </div>

                {/* Supporting Documents */}
                {selectedStaffFeedback.supportingDocuments && selectedStaffFeedback.supportingDocuments.length > 0 && (
                  <div>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Supporting Documents ({selectedStaffFeedback.supportingDocuments.length})</h3>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {selectedStaffFeedback.supportingDocuments.map((doc, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            padding: '1rem', 
                            background: 'var(--gray-50)', 
                            borderRadius: '0.5rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between' 
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <FaDownload style={{ color: 'var(--primary)' }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{doc.fileName}</div>
                              <div style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                                {(doc.fileSize / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-primary btn-sm"
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Digital Signature */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedStaffFeedback.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>

                {/* Status & Admin Response */}
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Status & Admin Response</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${
                      selectedStaffFeedback.status === 'Resolved' ? 'badge-success' :
                      selectedStaffFeedback.status === 'Escalated' ? 'badge-danger' :
                      selectedStaffFeedback.status === 'Under Review' ? 'badge-warning' :
                      selectedStaffFeedback.status === 'Closed' ? 'badge-success' :
                      'badge-primary'
                    }`}>
                      {selectedStaffFeedback.status || 'New'}
                    </span>
                  </div>
                  {selectedStaffFeedback.adminNotes && (
                    <div>
                      <strong>Admin Notes:</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--secondary)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedStaffFeedback.adminNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingStaffFeedback(selectedStaffFeedback);
                setResolveStaffData({ status: selectedStaffFeedback.status || 'New', adminNotes: selectedStaffFeedback.adminNotes || '' });
                setSelectedStaffFeedback(null);
              }} className="btn btn-success" style={{ background: '#10b981', borderColor: '#10b981' }}>
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedStaffFeedback(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Log Detail Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Visitor Log Details</h2>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Visitor Information
                    {selectedLog.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Full Name:</strong><br />
                      {selectedLog.fullName}
                    </div>
                    <div>
                      <strong>Phone:</strong><br />
                      {selectedLog.phoneNumber}
                    </div>
                    <div>
                      <strong>Email:</strong><br />
                      {selectedLog.email}
                    </div>
                    <div>
                      <strong>Relationship:</strong><br />
                      {selectedLog.relationshipToResident}
                      {selectedLog.relationshipOther && ` (${selectedLog.relationshipOther})`}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Visit Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Date:</strong><br />
                      {new Date(selectedLog.visitDate).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>Time:</strong><br />
                      {selectedLog.visitTime}
                    </div>
                    <div>
                      <strong>Resident Name:</strong><br />
                      {selectedLog.residentName}
                    </div>
                    <div>
                      <strong>Reason for Visit:</strong><br />
                      {selectedLog.reasonForVisit}
                      {selectedLog.reasonOther && ` (${selectedLog.reasonOther})`}
                    </div>
                    <div>
                      <strong>Visit Type:</strong><br />
                      <span className="badge badge-primary">{selectedLog.visitType}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <strong>Experience Rating:</strong><br />
                      <span style={{ fontSize: '1.5rem', color: '#fbbf24' }}>
                        {'★'.repeat(selectedLog.experienceRating)}{'☆'.repeat(5 - selectedLog.experienceRating)}
                      </span>
                      <span style={{ marginLeft: '0.5rem' }}>{selectedLog.experienceRating} / 5</span>
                    </div>
                    <div>
                      <strong>Purpose Resolved:</strong><br />
                      <span className={`badge ${selectedLog.purposeResolved === 'Yes' ? 'badge-success' : selectedLog.purposeResolved === 'No' ? 'badge-danger' : 'badge-warning'}`}>
                        {selectedLog.purposeResolved}
                      </span>
                    </div>
                  </div>
                  {selectedLog.comments && (
                    <div>
                      <strong>Comments:</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedLog.comments}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Status & Admin Response</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${selectedLog.status === 'Resolved' || selectedLog.status === 'Closed' ? 'badge-success' : selectedLog.status === 'In Progress' ? 'badge-warning' : 'badge-secondary'}`}>
                      {selectedLog.status || 'Pending'}
                    </span>
                  </div>
                  {selectedLog.addressFeedback && (
                    <div>
                      <strong>Address Feedback (Admin):</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedLog.addressFeedback}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedLog.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingLog(selectedLog);
                setResolveLogData({ status: selectedLog.status || 'Pending', addressFeedback: selectedLog.addressFeedback || '' });
                setSelectedLog(null);
              }} className="btn btn-success" style={{ background: '#10b981', borderColor: '#10b981' }}>
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedLog(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Resident Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="modal-overlay" onClick={() => setSelectedFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resident Feedback Details</h2>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>
                    Resident Information
                    {selectedFeedback.isAnonymous && (
                      <span className="badge badge-secondary" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>Anonymous</span>
                    )}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Resident Name:</strong><br />
                      {selectedFeedback.residentFullName}
                    </div>
                    {selectedFeedback.guardianParentName && (
                      <div>
                        <strong>Guardian/Parent:</strong><br />
                        {selectedFeedback.guardianParentName}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Feedback Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <strong>Submitted:</strong><br />
                      {new Date(selectedFeedback.submittedAt).toLocaleDateString()} at {new Date(selectedFeedback.submittedAt).toLocaleTimeString()}
                    </div>
                    <div>
                      <strong>Type:</strong><br />
                      <span className={`badge ${selectedFeedback.feedbackType === 'Complaint' ? 'badge-danger' : selectedFeedback.feedbackType === 'Appreciation' ? 'badge-success' : 'badge-primary'}`}>
                        {selectedFeedback.feedbackType}
                      </span>
                    </div>
                    <div>
                      <strong>Experience Rating:</strong><br />
                      {selectedFeedback.experienceRating}
                    </div>
                    <div>
                      <strong>Service Area:</strong><br />
                      {selectedFeedback.serviceArea}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Detailed Feedback</h3>
                  <div style={{ padding: '1.5rem', background: 'var(--gray-50)', borderRadius: '0.5rem', lineHeight: '1.6' }}>
                    {selectedFeedback.feedbackDetails}
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Digital Signature</h3>
                  <div style={{ border: '2px solid var(--gray-300)', borderRadius: '0.5rem', padding: '1rem', background: 'white' }}>
                    <img src={selectedFeedback.digitalSignature} alt="Signature" style={{ maxWidth: '100%', height: 'auto' }} />
                  </div>
                </div>

                <div>
                  <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Status & Admin Response</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${selectedFeedback.status === 'Resolved' || selectedFeedback.status === 'Closed' ? 'badge-success' : selectedFeedback.status === 'In Progress' ? 'badge-warning' : selectedFeedback.status === 'Acknowledged' ? 'badge-primary' : 'badge-secondary'}`}>
                      {selectedFeedback.status || 'Pending'}
                    </span>
                  </div>
                  {selectedFeedback.adminNotes && (
                    <div>
                      <strong>Admin Notes:</strong><br />
                      <div style={{ padding: '1rem', background: 'var(--secondary)', borderRadius: '0.5rem', marginTop: '0.5rem' }}>
                        {selectedFeedback.adminNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setResolvingFeedback(selectedFeedback);
                setResolveFeedbackData({ status: selectedFeedback.status || 'Pending', adminNotes: selectedFeedback.adminNotes || '' });
                setSelectedFeedback(null);
              }} className="btn btn-success" style={{ background: '#10b981', borderColor: '#10b981' }}>
                Resolve / Update Status
              </button>
              <button onClick={() => setSelectedFeedback(null)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Resolve Visitor Log Modal */}
      {resolvingLog && (
        <div className="modal-overlay" onClick={() => setResolvingLog(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Visitor Log</h2>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label"><strong>Status</strong></label>
                <select className="form-select" value={resolveLogData.status} onChange={e => setResolveLogData(d => ({ ...d, status: e.target.value }))}>
                  <option value="Pending">Pending</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label"><strong>Address Feedback / Remarks</strong></label>
                <textarea className="form-textarea" rows={4} maxLength={2000} value={resolveLogData.addressFeedback} onChange={e => setResolveLogData(d => ({ ...d, addressFeedback: e.target.value }))} placeholder="Add remarks or address feedback..." />
                <div style={{ fontSize: '0.75rem', color: 'var(--gray)', textAlign: 'right' }}>{resolveLogData.addressFeedback.length}/2000</div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveLog} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingLog(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Resident Feedback Modal */}
      {resolvingFeedback && (
        <div className="modal-overlay" onClick={() => setResolvingFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Resident Feedback</h2>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label"><strong>Status</strong></label>
                <select className="form-select" value={resolveFeedbackData.status} onChange={e => setResolveFeedbackData(d => ({ ...d, status: e.target.value }))}>
                  <option value="Pending">Pending</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label"><strong>Admin Notes / Remarks</strong></label>
                <textarea className="form-textarea" rows={4} maxLength={2000} value={resolveFeedbackData.adminNotes} onChange={e => setResolveFeedbackData(d => ({ ...d, adminNotes: e.target.value }))} placeholder="Add notes or remarks..." />
                <div style={{ fontSize: '0.75rem', color: 'var(--gray)', textAlign: 'right' }}>{resolveFeedbackData.adminNotes.length}/2000</div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveFeedback} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingFeedback(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Staff Feedback Modal */}
      {resolvingStaffFeedback && (
        <div className="modal-overlay" onClick={() => setResolvingStaffFeedback(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Resolve Staff Feedback</h2>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label"><strong>Status</strong></label>
                <select className="form-select" value={resolveStaffData.status} onChange={e => setResolveStaffData(d => ({ ...d, status: e.target.value }))}>
                  <option value="New">New</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Escalated">Escalated</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label"><strong>Admin Notes / Remarks</strong></label>
                <textarea className="form-textarea" rows={4} maxLength={2000} value={resolveStaffData.adminNotes} onChange={e => setResolveStaffData(d => ({ ...d, adminNotes: e.target.value }))} placeholder="Add notes or remarks..." />
                <div style={{ fontSize: '0.75rem', color: 'var(--gray)', textAlign: 'right' }}>{resolveStaffData.adminNotes.length}/2000</div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleResolveStaffFeedback} className="btn btn-primary">Save</button>
              <button onClick={() => setResolvingStaffFeedback(null)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerDashboard;